import {
  Component, OnInit, OnChanges, OnDestroy,
  AfterViewInit, Input, Output,
  EventEmitter, SimpleChanges, ViewChild, ElementRef,
  ChangeDetectionStrategy
}
         from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subscription, finalize } from 'rxjs';
import { FormGroup, FormControl } from '@angular/forms';
import { ConfirmDialogComponent }
    from '@app/secure/share/confirmDialog/confirmDialog.component';
import { ERROR } from '@app/constants/Error.constant';
import { DOCUMENT_VIEW_VALUE } from '@app/constants/Setting.constant';
import { NotificationService } from 
    '@app/secure/notification/notification.service';
import { DocumentService } from '@app/secure/service/document.service';
import { CyDateTime } from '@app/secure/service/CyDateTime.service';
import { ApiService } from '@app/secure/service/Api.service';
import { SettingService } from '@app/service/setting.service';
import
{
    DocumentApiFileRow,
    DocumentApiResponse,
    DocumentListCyData
} from
    '@app/secure/interface/DocumentServiceResponses.interface';
import { CyResponse } from '@app/secure/interface/CyResponse.interface';

@Component
({
    selector: 'document',
    templateUrl: './document.component.html',
    styleUrls: ['./document.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
class DocumentComponent  
          implements OnInit, OnChanges, OnDestroy, AfterViewInit 
{
    @Input() entityType: string;
    @Input() entityUId: string;
    @Input() entityPath: string;
    @Output() onUploadStart: EventEmitter<void> = new EventEmitter<void>();
    @Output() onUploadComplete: EventEmitter<void> = new EventEmitter<void>();
    @ViewChild('fileInput') fileInputRef: ElementRef<HTMLInputElement>;

    content: DocumentApiFileRow[] | null;
    
    // avatar custom css
    customStyle = 
    {
        backgroundColor: "#ffffff",
        border: "1px solid #7e7e7e",
        borderRadius: "50%",
        color: "#7e7e7e",
        cursor: "pointer"
    };
    
    /*
     * upload form 
    */
    uploadForm: FormGroup;
    
    docName: FormControl;
    docDescription: FormControl;
    
    uploadDisabled: boolean;
    
    newDocument = 
    {
        name : null,
        description: null
    };
        
    documents = [];
    allDocuments = [];
    filteredDocuments = [];
    selectedDocumentIds: number[] = [];
    documentViewMode: string = DOCUMENT_VIEW_VALUE.AVATAR;
    searchTextFC: FormControl;
    combineNameFC: FormControl;
    
    private subscriptions: Subscription[] = [];
    
    // Make CyDateTime accessible in template
    public CyDateTime = CyDateTime;
    
    /**
     * @desc   - component constructor
     * @access - public
     * @param  - 
     * @return - 
     */                
    constructor
    (            
        private router: Router,    
        private apiService: ApiService,
        private notificationSvc: NotificationService,
        private documentService: DocumentService,
        private settingService: SettingService,
        private dialog: MatDialog
    ) 
    {}    

    //______________________ P R I V A T E  M E T H O D S ________________________
    /**
     * @desc   - Add one file row to the in-memory document list
     * @access - private
     * @param  - object file : API file row
     * @return -
     */
    private pushDocumentFromFile
    (
        file
    ): void
    {
        let oneDocument;

        oneDocument = {};
        oneDocument.edit = true;
        oneDocument.id = file.id;
        oneDocument.entityType = file.entityType;
        oneDocument.entityUId = file.entityUId;
        oneDocument.fileTypeREF = file.fileTypeREF;
        oneDocument.path = file.path;
        oneDocument.name = file.name;
        oneDocument.description = file.description;
        oneDocument.byUserUId = file.byUserUId;
        oneDocument.createDT = file.createDT;
        this.allDocuments.push(oneDocument);
        this.applyDocumentFilter();
    }

    /**
     * @desc   - Remove one document from in-memory lists by id
     * @access - private
     * @param  - number documentId : file row id
     * @return -
     */
    private removeDocumentFromLists
    (
        documentId: number
    ): void
    {
        let i;
        let selectionIndex;

        for (i = this.allDocuments.length - 1; i >= 0; i--)
        {
            if (this.allDocuments[i].id === documentId)
            {
                this.allDocuments.splice(i, 1);
            }
        }

        selectionIndex = this.selectedDocumentIds.indexOf(documentId);
        if (selectionIndex >= 0)
        {
            this.selectedDocumentIds.splice(selectionIndex, 1);
        }

        this.applyDocumentFilter();
        this.syncCombineNameControl();
    }

    //______________________ P U B L I C  M E T H O D S ________________________
        
    /**
     * @desc   - initialization of the document page
     * @access - public
     * @param  -
     * @return - 
     */    
    ngOnInit()
    {
        let subscription: Subscription;

        /*
         * upload form
         */
        this.docName = new FormControl();
        this.docDescription = new FormControl();
        this.searchTextFC = new FormControl('');
        this.combineNameFC = new FormControl('');

        this.uploadForm = new FormGroup
        ({
            docName: this.docName,
            docDescription: this.docDescription
        });
        
        this.uploadDisabled = false;
        this.documentViewMode =
            this.settingService.getDocumentViewMode();

        subscription = this.settingService.settings$.subscribe(() =>
        {
            this.documentViewMode =
                this.settingService.getDocumentViewMode();
        });
        this.subscriptions.push(subscription);

        subscription = this.searchTextFC.valueChanges.subscribe(() =>
        {
            this.applyDocumentFilter();
        });
        this.subscriptions.push(subscription);

        // Subscribe to document list refresh events
        subscription = this.documentService.refreshDocumentList$
        .subscribe(() => 
        {
            this.updateDocumentList();
        });
        this.subscriptions.push(subscription);
    }
    
    /**
     * @desc   - changes to the document page
     * @access - public
     * @param  -
     * @return - 
     */    
    ngOnChanges
    (
        changes: SimpleChanges
    ) 
    {
        if (changes.entityUId && changes.entityUId.currentValue) 
        {
            this.updateDocumentList();
        }
    }
      
        
    /**
     * @desc   - angular function when the page is initialized
     * @access - public
     * @param  - 
     * @return - 
     */            
    ngAfterViewInit()
    {
        this.syncCombineNameControl();
    }

    /**
     * @desc   - Opens the hidden file input for document upload
     * @access - public
     * @param  -
     * @return -
     */
    public openFilePicker(): void
    {
        let fileInputEl: HTMLInputElement;
        let message: string;

        if (this.entityUId == null)
        {
            message = 'Save the record before adding a document.';
            this.notificationSvc.warning('Documents', message, 3000);
            return;
        }

        if (this.uploadDisabled)
        {
            return;
        }

        if (this.fileInputRef && this.fileInputRef.nativeElement)
        {
            fileInputEl = this.fileInputRef.nativeElement;
            fileInputEl.click();
        }
    }
    
    /**
     * @desc   - angular function when the page is closing
     * @access - public
     * @param  - 
     * @return - 
     */            
    ngOnDestroy()
    {
        // Clean up subscriptions
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    /**
     * @desc   - Whether upload form has in-progress draft state
     * @access - public
     * @param  -
     * @return - boolean
     */
    hasUploadFormDraft(): boolean
    {
        let result: boolean;
        let docNameVal: string;
        let docDescVal: string;
        let fileInputEl: HTMLInputElement;
        let hasFiles: boolean;

        result = false;
        docNameVal = '';
        docDescVal = '';
        hasFiles = false;

        if (this.uploadDisabled)
        {
            result = true;
        }
        else
        {
            docNameVal = this.docName.value == null ?
                '' : String(this.docName.value).trim();
            docDescVal = this.docDescription.value == null ?
                '' : String(this.docDescription.value).trim();
            if (docNameVal !== '' || docDescVal !== '')
            {
                result = true;
            }
            else if (this.fileInputRef &&
                this.fileInputRef.nativeElement)
            {
                fileInputEl = this.fileInputRef.nativeElement;
                hasFiles = !!(fileInputEl.files &&
                    fileInputEl.files.length > 0);
                if (hasFiles)
                {
                    result = true;
                }
            }
        }

        return result;
    }

    /**
     * @desc   - Resets upload form fields and file selection
     * @access - public
     * @param  -
     * @return - void
     */
    clearUploadForm(): void
    {
        let i: number;
        let fileInputEl: HTMLInputElement;

        this.docName.setValue('');
        this.docDescription.setValue('');
        this.uploadDisabled = false;
        if (this.fileInputRef && this.fileInputRef.nativeElement)
        {
            fileInputEl = this.fileInputRef.nativeElement;
            fileInputEl.value = '';
        }
        for (i = 0; i < this.documents.length; i++)
        {
            this.documents[i].edit = true;
        }
    }

    /**
     * @desc   - onvert the input value of the docName form control to uppercase
     * @access - public
     * @param  -
     * @return - 
     */
    onDocNameInput
    (): void 
    {
        let docNameControl;
        let upperCaseValue;
        let rawValue: string;

        docNameControl = this.uploadForm.get('docName');
        rawValue = docNameControl.value == null ?
            '' : String(docNameControl.value).trim();
        if (rawValue !== '')
        {
            upperCaseValue = String(docNameControl.value).toUpperCase();
            docNameControl.setValue(upperCaseValue, { emitEvent: false });
        }
    }

    /**
     * @desc   - onvert the input value of the docDescription form control to uppercase
     * @access - public
     * @param  -
     * @return -
     */
    onDocDescriptionInput
    (): void 
    {
        let docDescriptionControl;
        let upperCaseValue;
        let rawValue: string;

        docDescriptionControl = this.uploadForm.get('docDescription');
        rawValue = docDescriptionControl.value == null ?
            '' : String(docDescriptionControl.value).trim();
        if (rawValue !== '')
        {
            upperCaseValue =
                String(docDescriptionControl.value).toUpperCase();
            docDescriptionControl.setValue(
                upperCaseValue,
                { emitEvent: false }
            );
        }
    }

    /**
     * @desc   - Whether list view mode is active
     * @access - public
     * @param  -
     * @return - boolean
     */
    isListView(): boolean
    {
        let result;

        result = this.documentViewMode === DOCUMENT_VIEW_VALUE.LIST;

        return result;
    }

    /**
     * @desc   - Apply name and owner search filter to documents
     * @access - public
     * @param  -
     * @return -
     */
    applyDocumentFilter(): void
    {
        let searchValue;
        let normalizedSearch;
        let filtered;
        let i;
        let oneDoc;
        let nameValue;
        let ownerValue;

        searchValue = this.searchTextFC.value == null ?
            '' : String(this.searchTextFC.value).trim();
        normalizedSearch = searchValue.toUpperCase();
        filtered = [];

        for (i = 0; i < this.allDocuments.length; i++)
        {
            oneDoc = this.allDocuments[i];
            if (normalizedSearch === '')
            {
                filtered.push(oneDoc);
            }
            else
            {
                nameValue = oneDoc.name == null ?
                    '' : String(oneDoc.name).toUpperCase();
                ownerValue = oneDoc.byUserUId == null ?
                    '' : String(oneDoc.byUserUId).toUpperCase();
                if (
                    nameValue.indexOf(normalizedSearch) >= 0 ||
                    ownerValue.indexOf(normalizedSearch) >= 0
                )
                {
                    filtered.push(oneDoc);
                }
            }
        }

        this.filteredDocuments = filtered;
        this.documents = filtered;
    }

    /**
     * @desc   - Toggle list-row selection for combine
     * @access - public
     * @param  - number documentId : file row id
     * @param  - boolean checked : selection state
     * @return -
     */
    toggleDocumentSelection
    (
        documentId: number,
        checked: boolean
    ): void
    {
        let index;

        index = this.selectedDocumentIds.indexOf(documentId);
        if (checked === true && index < 0)
        {
            this.selectedDocumentIds.push(documentId);
        }
        else if (checked === false && index >= 0)
        {
            this.selectedDocumentIds.splice(index, 1);
        }

        this.syncCombineNameControl();
    }

    /**
     * @desc   - Whether a document row is selected
     * @access - public
     * @param  - number documentId : file row id
     * @return - boolean
     */
    isDocumentSelected
    (
        documentId: number
    ): boolean
    {
        let result;

        result = this.selectedDocumentIds.indexOf(documentId) >= 0;

        return result;
    }

    /**
     * @desc   - Whether file type can be merged into a combined PDF
     * @access - public
     * @param  - string fileTypeREF : stored file extension
     * @return - boolean
     */
    isCombineSupportedFileType
    (
        fileTypeREF: string
    ): boolean
    {
        let normalized;
        let result;

        normalized = fileTypeREF == null ?
            '' : String(fileTypeREF).trim().toLowerCase();
        if (
            normalized === 'pdf' ||
            normalized === 'png' ||
            normalized === 'jpeg' ||
            normalized === 'jpg'
        )
        {
            result = true;
        }
        else
        {
            result = false;
        }

        return result;
    }

    /**
     * @desc   - Whether selected rows can be combined (2+ PDF/PNG/JPEG)
     * @access - public
     * @param  -
     * @return - boolean
     */
    canCombineSelectedDocuments(): boolean
    {
        let result;
        let allSupported;
        let i;
        let j;
        let docId;
        let oneDoc;
        let found;

        result = false;
        allSupported = true;
        if (this.selectedDocumentIds.length >= 2)
        {
            for (i = 0;
                i < this.selectedDocumentIds.length && allSupported;
                i++)
            {
                docId = this.selectedDocumentIds[i];
                found = false;
                oneDoc = null;
                for (j = 0; j < this.allDocuments.length; j++)
                {
                    if (this.allDocuments[j].id === docId)
                    {
                        oneDoc = this.allDocuments[j];
                        found = true;
                        break;
                    }
                }
                if (
                    !found ||
                    !this.isCombineSupportedFileType(oneDoc.fileTypeREF)
                )
                {
                    allSupported = false;
                }
            }
            if (allSupported)
            {
                result = true;
            }
        }

        return result;
    }

    /**
     * @desc   - Enable/disable archive name control with selection
     * @access - private
     * @param  -
     * @return -
     */
    private syncCombineNameControl(): void
    {
        if (this.canCombineSelectedDocuments())
        {
            this.combineNameFC.enable({ emitEvent: false });
        }
        else
        {
            this.combineNameFC.disable({ emitEvent: false });
        }
    }

    /**
     * @desc   - Archive selected PDF/PNG/JPEG documents into one PDF
     * @access - public
     * @param  -
     * @return -
     */
    combineSelectedDocuments(): void
    {
        let combineName;
        let entityPathStr;
        let pathValue;
        let postData;
        let subscription;
        let canCombine;

        canCombine = false;
        combineName = '';
        entityPathStr = '';
        pathValue = '';
        postData = null;
        subscription = null;

        canCombine = this.canCombineSelectedDocuments();
        if (canCombine)
        {
            combineName = this.combineNameFC.value == null ?
                '' : String(this.combineNameFC.value).trim();

            entityPathStr = typeof this.entityPath === 'string' ?
                this.entityPath : '';
            if (entityPathStr.trim() !== '')
            {
                pathValue = entityPathStr;
            }
            else if (this.entityUId)
            {
                pathValue = String(this.entityUId);
            }
            else
            {
                pathValue = '';
            }

            postData =
            {
                entityType: this.entityType,
                entityUId: this.entityUId,
                entityPath: pathValue,
                fileIds: this.selectedDocumentIds,
                documentName: combineName === '' ?
                    null : combineName
            };

            this.onUploadStart.emit();
            subscription = this.documentService.combineDocuments(postData)
            .pipe(
                finalize(() =>
                {
                    this.onUploadComplete.emit();
                })
            )
            .subscribe(
            {
                next: (response) =>
                {
                    if (
                        response &&
                        response.error === ERROR.NO_ERROR &&
                        response.cyData?.file
                    )
                    {
                        this.pushDocumentFromFile(response.cyData.file);
                        this.selectedDocumentIds = [];
                        this.combineNameFC.setValue('');
                        this.syncCombineNameControl();
                        this.notificationSvc.info(
                            'Archive Documents',
                            'Documents archived successfully.',
                            3000
                        );
                    }
                    else
                    {
                        this.notificationSvc.error(
                            'Archive Documents',
                            'Could not archive selected documents.',
                            5000
                        );
                    }
                },
                error: (err) =>
                {
                    console.error(err);
                    this.notificationSvc.error(
                        'Archive Documents',
                        'Archive request failed.',
                        5000
                    );
                }
            });
            this.subscriptions.push(subscription);
        }
        else
        {
            this.notificationSvc.warning(
                'Archive Documents',
                'Select at least two PDF, PNG, or JPEG documents '
                + 'to archive.',
                4000
            );
        }
    }

    /**
     * @desc   - check if the form is modified
     * @access - public
     * @param  - 
     * @return - 
     */ 
    checkDirty
    (
        originalValue: string, 
        formControlName: string
    ): void 
    {
        let control;

        control = this.uploadForm.get(formControlName);
        if (originalValue === control.value) 
        {
            control.markAsPristine();
        }
    }
    
    /**
     * @desc   - update the docucment name and description
     * @access - private
     * @param  - string documentId : document DB id 
     * @param  - string documentName : document name 
     * @param  - string documentDescription : document description 
     * @return - 
     */            
    private async updateDocument
    (
        documentId,    
        documentName,
        documentDescription
    )
    {
        let documentData;
        let url;
        let data;
        let svcResponse;
        let response;

        try
        {
            data = 
            {
                documentId: documentId,    
                documentName: documentName,
                documentDescription: documentDescription
            };
            
            response = {};
            svcResponse = await this.apiService.postRaw<DocumentListCyData>(
                'document/updateDocument',
                data
            ).toPromise();
            
            if (svcResponse.error == ERROR.NO_ERROR)
            {
                response.error = ERROR.NO_ERROR;
                response.byUserUId = svcResponse.cyData?.byUserUId;
            }
            else
            {
                response.error = ERROR.UPDATE_FAILED;    
            }
        }
        catch (e)
        {
            console.log(e);
            response.error = ERROR.UPDATE_FAILED;
        }
        
        return response;        
    }

    /**
     * @desc   - delete the docucment
     * @access - private
     * @param  - string documentId : document DB id 
     * @return - 
     */            
    private async deleteDocumentService
    (
        documentId    
    )
    {
        let documentData;
        let url;
        let data;
        let svcResponse;
        let response;

        try
        {
            data = 
            {
                documentId: documentId,    
            };
            
            response = {};
            svcResponse = await this.apiService.postRaw<DocumentListCyData>(
                'document/deleteDocument',
                data
            ).toPromise();
            
            if (svcResponse.error == ERROR.NO_ERROR)
            {
                response.error = ERROR.NO_ERROR;
            }
            else
            {
                response.error = ERROR.UPDATE_FAILED;    
            }
        }
        catch (e)
        {
            console.log(e);
            response.error = ERROR.UPDATE_FAILED;
        }
        
        return response;        
    }
    
    /**
     * @desc   - update the document list
     * @access - public
     * @param  - 
     * @return - 
     */            
    public async updateDocumentList()
    {
        let i;
        let document;
        let documentData;
        let fileTypeRef;
        let url;
        let data;
        let svcResponse;
        let response;

        try
        {
            // TODO - need to define constant for file type 
            data = {entityType: this.entityType,
                    entityUId: this.entityUId
            };
            
            response = {};
            svcResponse = await this.apiService.postRaw<DocumentListCyData>(
                'document/getDocuments',
                data
            ).toPromise();
            
            if (svcResponse.error == ERROR.NO_ERROR)
            {
                response.error = ERROR.NO_ERROR;
                if (svcResponse.cyData.files == null)
                {
                    response.error = ERROR.NO_MATCH 
                }
                else
                {
                    response.error = ERROR.NO_ERROR;
                    response.files = svcResponse.cyData.files;
                    
                    // reset the documents
                    this.allDocuments = [];
                    this.selectedDocumentIds = [];
                    this.combineNameFC.setValue('');
                    this.syncCombineNameControl();
                    
                    for (i=0;i<response.files.length;i++)
                    {
                        documentData = response.files[i];
                        fileTypeRef = documentData &&
                            documentData.fileTypeREF ?
                            String(documentData.fileTypeREF).toLowerCase() :
                            '';

                        if (fileTypeRef == 'html')
                        {
                            continue;
                        }

                        document = {};
                        document.edit = true;                        
                        document.id = documentData.id;
                        document.entityUId = documentData.entityUId;
                        document.entityType = documentData.entityType;
                        document.fileTypeREF = documentData.fileTypeREF;
                        document.path = documentData.path;
                        document.name = documentData.name;
                        document.description = documentData.description;
                        document.byUserUId = documentData.byUserUId;
                        document.createDT = documentData.createDT;
                        this.allDocuments.push(document);
                    }
                    this.applyDocumentFilter();
                }
            }
            else
            {
                response.error = ERROR.MATCH_FAILED;    
            }
        }
        catch(e)
        {
            console.log(e);
            response.error = ERROR.MATCH_FAILED;
        }
        
        return response;
    }
    
    /**
    * @desc   - get document type
    * @access - public
    * @param  - string fileTypeREF : file type for the document
    * @return - 
    */            
    private getBlobType
    (
        fileTypeREF
    )
    {
        let blobType;
        let normalized;

        normalized = fileTypeREF == null ?
            '' : String(fileTypeREF).trim().toLowerCase();

        if (normalized == 'png')
        {
            blobType = 'image/png';
        }
        else if (normalized == 'pdf')
        {
            blobType = 'application/pdf';
        }
        else if (normalized == 'html')
        {
            blobType = 'text/html';
        }
        else if (normalized == 'jpeg' || normalized == 'jpg')
        {
             blobType = 'image/jpeg';
        }
        else if (normalized == 'docx')
        {
            blobType =
                'application/vnd.openxmlformats-officedocument.' +
                'wordprocessingml.document';
        }
        else if (normalized == 'doc')
        {
            blobType = 'application/msword';
        }
        else
        {
            blobType =  '';
        }

        return blobType;
    }
                    
    /**
    * @desc   - get the document content
    * @access - private
    * @param  - object oneDocument : document object
    * @return - 
    */            
    private getDocumentContent
    (
        oneDocument
    )
    {
        this.documentService
        .getDocumentContent(oneDocument)
        .subscribe((data: Blob) =>
        {
            let blobType;
            let file;
            let fileURL;
            let link;
            let downloadName;
            let normalizedType;
            let isOffice;

            blobType = this.getBlobType(oneDocument.fileTypeREF);
            file = new Blob([data], {type: blobType});
            fileURL = URL.createObjectURL(file);
            normalizedType = oneDocument &&
                oneDocument.fileTypeREF != null ?
                String(oneDocument.fileTypeREF)
                    .trim().toLowerCase() : '';
            isOffice = normalizedType === 'docx' ||
                normalizedType === 'doc';
            this.onUploadComplete.emit();

            if (isOffice)
            {
                downloadName = oneDocument.fileName ||
                    oneDocument.name ||
                    ('document.' + normalizedType);
                link = document.createElement('a');
                link.href = fileURL;
                link.download = downloadName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(function ()
                {
                    URL.revokeObjectURL(fileURL);
                }, 1000);
            }
            else
            {
                window.open(fileURL);
            }
        },
        (error) =>
        {
            console.log('Download File has failed: ', error);
        });
    }
    
    /**
     * @desc   - select the files 
     * @access - public
     * @param  - object event: event object for file selection
     * @return - 
     */            
    onFileSelected
    (
        event: Event
    ): void 
    {
        let files: FileList;
        let pathValue: string;
        let entityPathStr: string;
        let formData: FormData;
        let i: number;
        let msg: string;
        let nameTrim: string;
        let descTrim: string;
        let fileInputEl: HTMLInputElement;
        let file: File | DocumentApiFileRow | null;
        let oneDocument: DocumentApiFileRow;
        let failMsg: string;

        msg = `Document was successfully uploaded. ` +
            `Please wait to complete the processing`;
        files = null;
        pathValue = '';
        entityPathStr = '';

        files = event && (event.target as HTMLInputElement) && (event.target as HTMLInputElement).files ?
            (event.target as HTMLInputElement).files : null;

        if (files && files.length > 0)
        {
            entityPathStr = typeof this.entityPath === 'string' ?
                this.entityPath : '';
            if (entityPathStr.trim() !== '')
            {
                pathValue = entityPathStr;
            }
            else if (this.entityUId)
            {
                pathValue = String(this.entityUId);
            }
            else
            {
                pathValue = '';
            }

            if (!this.entityUId || pathValue === '')
            {
                this.notificationSvc.error(
                    'Store Document',
                    'Save the case motion before uploading documents.',
                    5000
                );
                if (this.fileInputRef && this.fileInputRef.nativeElement)
                {
                    fileInputEl = this.fileInputRef.nativeElement;
                    fileInputEl.value = '';
                }
            }
            else
            {
                this.onUploadStart.emit();
                formData = new FormData();
                formData.append('entityType', this.entityType);
                formData.append('entityUId', this.entityUId);
                formData.append('entityPath', pathValue);
                nameTrim = this.docName.value == null ?
                    '' : String(this.docName.value).trim();
                if (nameTrim !== '')
                {
                    formData.append('documentName', this.docName.value);
                }
                descTrim = this.docDescription.value == null ?
                    '' : String(this.docDescription.value).trim();
                if (descTrim !== '')
                {
                    formData.append(
                        'documentDescription',
                        this.docDescription.value
                    );
                }
                for (i = 0; i < files.length; i++)
                {
                    formData.append('files', files[i]);
                }

                this.apiService.postRawFormData<DocumentListCyData>(
                    'document/storeDocument',
                    formData
                )
                .pipe(
                    finalize(() =>
                    {
                        this.onUploadComplete.emit();
                    })
                )
                .subscribe(
                {
                    next: (response: CyResponse<DocumentListCyData>) =>
                    {
                        let uploadedFiles;
                        let fileIndex;

                        file = response?.cyData?.file || null;

                        if (
                            !response ||
                            response.error !== ERROR.NO_ERROR ||
                            !file ||
                            (file as { id?: string }).id === undefined ||
                            (file as { id?: string }).id === null
                        )
                        {
                            failMsg =
                                'Document upload did not complete. ' +
                                'Storage may be unavailable or ' +
                                'access denied.';
                            this.notificationSvc.error(
                                'Store Document',
                                failMsg,
                                5000
                            );
                        }
                        else
                        {
                            uploadedFiles = response?.cyData?.files;
                            if (
                                Array.isArray(uploadedFiles) &&
                                uploadedFiles.length > 0
                            )
                            {
                                for (
                                    fileIndex = 0;
                                    fileIndex < uploadedFiles.length;
                                    fileIndex++
                                )
                                {
                                    this.pushDocumentFromFile(
                                        uploadedFiles[fileIndex]
                                    );
                                }
                            }
                            else
                            {
                                this.pushDocumentFromFile(file);
                            }

                            this.notificationSvc.info(
                                'Store Document',
                                msg,
                                3000
                            );

                            this.docName.setValue('');
                            this.docDescription.setValue('');
                            if (this.fileInputRef &&
                                this.fileInputRef.nativeElement)
                            {
                                fileInputEl =
                                    this.fileInputRef.nativeElement;
                                fileInputEl.value = '';
                            }
                        }
                    },
                    error: (err) =>
                    {
                        this.notificationSvc.error(
                            'Store Document',
                            'Document upload failed. Please try again.',
                            5000
                        );
                        console.log(
                            'Store document request failed: ',
                            err
                        );
                    }
                });
            }
        }
    }
    
    /**
     * @desc   - handle avatar clicks
     * @access - public
     * @param  - object event: event object for avatar selection
     * @param  - object document: document OBJECT 
     * @return - 
     */            
    avatarClicked
    (
        event: Event, 
        oneDocument
    )
    {
        this.onUploadStart.emit();
        this.getDocumentContent(oneDocument);
    }

    /**
     * @desc   - get document type
     * @access - public
     * @param  - string fileTypeREF : file type for the document
     * @return - 
     */            
    getDocumentFileType
    (
        fileTypeREF
    )
    {
        let fileTypeLabel;
        
        if (fileTypeREF == 'png')
        {
            fileTypeLabel = 'P N G';
        }
        else if (fileTypeREF == 'pdf')
        {
            fileTypeLabel = 'P D F';
        }
        else if (fileTypeREF == 'html')
        {
            fileTypeLabel = 'H T M';
        }
        else if (fileTypeREF == 'jpeg')
        {
            fileTypeLabel = 'J P E';
        } 
        else if (fileTypeREF == 'jpg')
        {
            fileTypeLabel = 'J P G';
        }
        else
        {
            fileTypeLabel =  'U N K';
        }
        
        return fileTypeLabel;
    }
        
    /**
     * @desc   - get document label
     * @access - public
     * @param  - 
     * @return - 
     */            
    getDocumentLabel
    (
        type
    )
    {
        let label;
        
        if (type == 'VIN')
        {
            label = 'VIN';
        }
        else if (type == 'CASE')
        {
            label = 'CASE';
        }    
        
        return label;
    }
    
    /**
     * @desc   - determine if the row is new.
     *           Eac row has maximum of 4 columns
     * @access - public
     * @param  - 
     * @return - 
     */            
    isNewRow
    (
        index
    )
    {
        let newRow
        let rowNum;
        
        if (index >0)
        {
            rowNum = index % 4;
            
            if (rowNum == 0)
            {
                newRow = true;        
            }
            else
            {
                newRow = false;
            }
        }
        else
        {
            newRow = false;
        }
        
        return newRow;
    }
    
    /**
     * @desc   - Tooltip text for document description on hover
     * @access - public
     * @param  - object oneDocument : document row
     * @return - string
     */
    getDocumentDescriptionTooltip
    (
        oneDocument
    ): string
    {
        let result;
        let descriptionValue;

        result = '';
        descriptionValue = oneDocument == null ||
            oneDocument.description == null ?
            '' : String(oneDocument.description).trim();
        if (descriptionValue !== '')
        {
            result = descriptionValue;
        }

        return result;
    }

    /**
     * @desc   - Whether document description tooltip should show
     * @access - public
     * @param  - object oneDocument : document row
     * @return - boolean
     */
    hasDocumentDescriptionTooltip
    (
        oneDocument
    ): boolean
    {
        let result;

        result = this.getDocumentDescriptionTooltip(oneDocument) !== '';

        return result;
    }

    /**
     * @desc   - Normalize inline edit name to uppercase
     * @access - public
     * @param  - object oneDocument : document row
     * @param  - Event event : input event
     * @return -
     */
    onEditNameInput
    (
        oneDocument,
        event
    ): void
    {
        let rawValue;
        let upperCaseValue;

        rawValue = event && event.target && event.target.value == null ?
            '' : String(event.target.value);
        upperCaseValue = rawValue.toUpperCase();
        oneDocument.editName = upperCaseValue;
        if (event && event.target)
        {
            event.target.value = upperCaseValue;
        }
    }

    /**
     * @desc   - Normalize inline edit description to uppercase
     * @access - public
     * @param  - object oneDocument : document row
     * @param  - Event event : input event
     * @return -
     */
    onEditDescriptionInput
    (
        oneDocument,
        event
    ): void
    {
        let rawValue;
        let upperCaseValue;

        rawValue = event && event.target && event.target.value == null ?
            '' : String(event.target.value);
        upperCaseValue = rawValue.toUpperCase();
        oneDocument.editDescription = upperCaseValue;
        if (event && event.target)
        {
            event.target.value = upperCaseValue;
        }
    }

    /**
     * @desc   - Start inline rename for one document row
     * @access - public
     * @param  - number index : row index
     * @param  - object document : document row
     * @return -
     */
    editDocument
    (
        index,
        document
    )
    {
        let i;
        let nameValue;
        let descriptionValue;

        nameValue = document.name == null ?
            '' : String(document.name);
        descriptionValue = document.description == null ?
            '' : String(document.description);

        for (i = 0; i < this.documents.length; i++)
        {
            this.documents[i].edit = true;
        }

        document.edit = false;
        document.editName = nameValue;
        document.editDescription = descriptionValue;
        this.documents[index] = document;
    }
        
    /**
     * @desc   - save the document
     * @access - public
     * @param  - 
     * @return - 
     */            
    async saveDocument
    (
        index,
        document
    )
    {
        let i;
        let updateResult;
        
        let msg = `Document was successfully updated. ` + 
                  `Please wait to complete the processing`;
        let failedMsg = `Document update has failed.`; 
               
        document.name = document.editName == null ?
            '' : String(document.editName).trim();
        document.description = document.editDescription == null ?
            '' : String(document.editDescription).trim();

        this.onUploadStart.emit();
        updateResult = await this.updateDocument(document.id,
                                                 document.name,
                                                 document.description);
        this.onUploadComplete.emit();

        if (updateResult.error == ERROR.NO_ERROR)
        {
            this.notificationSvc.info('Update Document', msg, 3000);

            setTimeout(() =>
            {
                for (i = 0; i < this.documents.length; i++)
                {
                    this.documents[i].edit = true;
                }

                document.edit = true;
                if (updateResult.byUserUId)
                {
                    document.byUserUId = updateResult.byUserUId;
                }
                this.documents[index] = document;
            }, 3010);
        }
        else
        {
            // change title base on file.entityType
            this.notificationSvc.error('Update Document', failedMsg, 3000);
        }
    }

    /**
     * @desc   - save the document
     * @access - public
     * @param  - 
     * @return - 
    */            
    cancelEdit
    (
        index,
        document
    )
    {
        let i;
      
        for (i=0;i<this.documents.length;i++)
        {
            this.documents[i].edit = true;
        }
      
        document.edit = true;
        document.editName = '';
        document.editDescription = '';
        this.documents[index] = document;
    }
        
    /**
     * @desc   - Confirm before deleting a document row
     * @access - public
     * @param  - number index : row index
     * @param  - object document : document row
     * @return -
     */
    confirmDeleteDocument
    (
        index,
        document
    ): void
    {
        let dialogRef;
        let message;
        let subscription;
        let documentName;

        message = '';
        documentName = document.name == null ?
            '' : String(document.name).trim();
        if (documentName !== '')
        {
            message =
                'Are you sure you want to delete "' +
                documentName + '"?';
        }
        else
        {
            message =
                'Are you sure you want to delete this document?';
        }

        dialogRef = this.dialog.open(ConfirmDialogComponent,
        {
            width: '300px',
            data:
            {
                title: 'Delete Document?',
                message: message
            },
        });

        subscription = dialogRef.afterClosed().subscribe((result) =>
        {
            if (result)
            {
                this.deleteDocument(index, document);
            }
        });
        this.subscriptions.push(subscription);
    }

    /**
     * @desc   - Delete one document row
     * @access - public
     * @param  - number index : row index
     * @param  - object document : document row
     * @return -
     */            
    async deleteDocument
    (
       index,
       document
    )
    {
        let i;
        let deleteResult;
        let msg = `Document was successfully deleted. ` + 
                  `Please wait to complete the processing`;
        let failedMsg = `Document update has failed.`; 
                        
        this.onUploadStart.emit();                
        deleteResult = await this.deleteDocumentService(document.id);
        this.onUploadComplete.emit();

        if (deleteResult.error == ERROR.NO_ERROR)
        {
            this.notificationSvc.info('Update Document', msg, 3000);
            setTimeout(() =>
            {
                for (i = 0; i < this.documents.length; i++)
                {
                    this.documents[i].edit = true;
                }

                document.edit = true;
                this.removeDocumentFromLists(document.id);
            }, 3010);
        }  
        else
        {
            // change title base on file.entityType
            this.notificationSvc.error('Delete Document', failedMsg, 3000);
        }
    }       
}
        
export { DocumentComponent }
